"use client";

import { motion, useScroll, useTransform } from "framer-motion";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { useRef } from "react";
import Link from "next/link";
import { getAboutHomeData } from "@/data/about";
import { useLanguage } from "@/lib/i18n";

export default function About() {
  const containerRef = useRef(null);
  const { language } = useLanguage();
  const aboutData = getAboutHomeData(language);

  // Efek Parallax
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  return (
    <section
      ref={containerRef}
      id="about"
      className="py-24 md:py-40 bg-white relative"
    >
      <div className="container mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row gap-16 md:gap-24 items-center">
          {/* KOLOM KIRI: TEKS */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="w-full md:w-1/2"
          >
            <span className="text-brand-gold text-xs font-bold tracking-[0.25em] uppercase block mb-6">
              {aboutData.tagline}
            </span>
            <h2 className="font-serif text-4xl md:text-6xl text-brand-charcoal mb-8 leading-[1.1] whitespace-pre-line">
              {aboutData.title}
            </h2>

            <div className="space-y-6 text-gray-500 font-light text-lg leading-relaxed mb-10">
              {aboutData.description.map((paragraph, idx) => (
                <p key={idx}>{paragraph}</p>
              ))}
            </div>

            <Link href={aboutData.cta.link}>
              <Button
                variant="outline"
                className="rounded-none border-brand-charcoal text-brand-charcoal hover:bg-brand-charcoal hover:text-white px-10 py-7 text-xs font-bold tracking-[0.2em] uppercase transition-all duration-300"
              >
                {aboutData.cta.text}
              </Button>
            </Link>
          </motion.div>

          {/* KOLOM KANAN: GAMBAR & PARALLAX */}
          <div className="w-full md:w-1/2 relative">
            <div className="relative w-full h-[500px] md:h-[600px] overflow-hidden">
              <motion.div
                style={{ y }}
                className="absolute w-full h-[120%] -top-[10%]"
              >
                <Image
                  src={aboutData.image}
                  alt="Interior Mewah"
                  fill
                  className="object-cover"
                />
              </motion.div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent pointer-events-none" />
            </div>

            {/* ELEMEN FLOATING */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 1 }}
              className="absolute bg-white shadow-[0_30px_60px_-15px_rgba(0,0,0,0.15)] z-20 p-8
                 -bottom-12 left-4 right-4
                 md:bottom-20 md:left-[-60px] md:right-auto md:w-[320px] md:p-10"
            >
              <p className="font-serif text-xl italic text-brand-charcoal mb-4 leading-relaxed">
                "{aboutData.testimonial.quote}"
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-[1px] bg-brand-gold" />
                <p className="text-xs font-bold uppercase tracking-widest text-gray-400">
                  {aboutData.testimonial.author} —{" "}
                  {aboutData.testimonial.role}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
